import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// ── Integration Config ──

export interface IntegrationConfig {
  id: string;
  tenant_id: string;
  provider_type: string;
  provider_name: string;
  client_id: string;
  client_secret: string; // masked in responses
  scopes: string[];
  redirect_uri: string;
  auth_endpoint?: string;
  token_endpoint?: string;
  is_active: boolean;
  config_data: string; // masked in responses
  description: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateIntegrationConfigRequest {
  provider_type: string;
  provider_name: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
  redirect_uri: string;
  auth_endpoint?: string;
  token_endpoint?: string;
  is_active: boolean;
  config_data: string;
  description: string;
}

export interface UpdateIntegrationConfigRequest {
  provider_name?: string;
  client_id?: string;
  client_secret?: string;
  scopes?: string[];
  redirect_uri?: string;
  auth_endpoint?: string;
  token_endpoint?: string;
  is_active?: boolean;
  config_data?: string;
  description?: string;
}

export interface IntegrationConfigFilters extends SearchParams {
  provider_type?: string;
}

// ── OAuth Token ──

export interface OAuthTokenInfo {
  id: string;
  integration_config_id: string;
  user_id: string;
  token_type: string;
  expires_at?: string;
  granted_scopes: string[];
  status: string;
  last_refreshed_at?: string;
  created_at: string;
}

export interface OAuthTokenFilters extends SearchParams {
  integration_config_id?: string;
  status?: string;
}

export interface OAuthAuthorizeResponse {
  authorization_url: string;
  state: string;
}

// ── Webhook Subscription ──

export interface WebhookSubscription {
  id: string;
  tenant_id: string;
  name: string;
  target_url: string;
  secret: string; // masked
  event_types: string[];
  is_active: boolean;
  description: string;
  headers: Record<string, string>;
  max_retries: number;
  retry_delay_seconds: number;
  last_triggered_at?: string;
  failure_count: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateWebhookSubscriptionRequest {
  name: string;
  target_url: string;
  secret: string;
  event_types: string[];
  is_active: boolean;
  description: string;
  headers?: Record<string, string>;
  max_retries: number;
  retry_delay_seconds: number;
}

export interface UpdateWebhookSubscriptionRequest {
  name?: string;
  target_url?: string;
  secret?: string;
  event_types?: string[];
  is_active?: boolean;
  description?: string;
  headers?: Record<string, string>;
  max_retries?: number;
  retry_delay_seconds?: number;
}

export interface WebhookSubscriptionFilters extends SearchParams {
  event_type?: string;
}

// ── Webhook Event ──

export interface WebhookEventInfo {
  id: string;
  subscription_id: string;
  event_type: string;
  target_url: string;
  status: string;
  http_status_code?: number;
  error_message?: string;
  attempt_count: number;
  max_attempts: number;
  next_retry_at?: string;
  delivered_at?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  created_at: string;
}

export interface WebhookEventFilters extends SearchParams {
  subscription_id?: string;
  event_type?: string;
  status?: string;
}

export interface WebhookTestResult {
  status_code?: number;
  response_body?: string;
  success: boolean;
  error?: string;
}

// ── Known Constants ──

export const PROVIDER_TYPES = ['google', 'microsoft', 'salesforce', 'custom'];

export const OAUTH_TOKEN_STATUSES = ['active', 'expired', 'revoked', 'error'];

export const WEBHOOK_EVENT_TYPES = [
  '*',
  'customer.created', 'customer.updated', 'customer.deleted',
  'contact.created', 'contact.updated', 'contact.deleted',
  'opportunity.created', 'opportunity.updated', 'opportunity.stage_changed',
  'opportunity.won', 'opportunity.lost',
  'order.created', 'order.status_changed',
  'ticket.created', 'ticket.status_changed',
];

export const WEBHOOK_EVENT_STATUSES = ['pending', 'sent', 'failed', 'retrying'];

// ── Integration Config API ──

export const getIntegrationConfigs = (params: IntegrationConfigFilters = {}) =>
  apiGet<PagedResponse<IntegrationConfig>>('/api/v1/integration-configs', params);

export const getIntegrationConfig = (id: string) =>
  apiGet<IntegrationConfig>(`/api/v1/integration-configs/${id}`);

export const createIntegrationConfig = (data: CreateIntegrationConfigRequest) =>
  apiPost<IntegrationConfig>('/api/v1/integration-configs', data);

export const updateIntegrationConfig = (id: string, data: UpdateIntegrationConfigRequest) =>
  apiPut<IntegrationConfig>(`/api/v1/integration-configs/${id}`, data);

export const deleteIntegrationConfig = (id: string) =>
  apiDelete(`/api/v1/integration-configs/${id}`);

// ── OAuth API ──

export const oauthAuthorize = (integrationConfigId: string, scopeOverrides?: string[]) =>
  apiPost<OAuthAuthorizeResponse>('/api/v1/oauth/authorize', {
    integration_config_id: integrationConfigId,
    scope_overrides: scopeOverrides,
  });

export const oauthCallback = (integrationConfigId: string, code: string, state: string) =>
  apiPost<OAuthTokenInfo>(`/api/v1/oauth/callback/${integrationConfigId}`, { code, state });

export const getOAuthTokens = (params: OAuthTokenFilters = {}) =>
  apiGet<PagedResponse<OAuthTokenInfo>>('/api/v1/oauth/tokens', params);

export const getOAuthToken = (id: string) =>
  apiGet<OAuthTokenInfo>(`/api/v1/oauth/tokens/${id}`);

export const refreshOAuthToken = (id: string) =>
  apiPost<OAuthTokenInfo>(`/api/v1/oauth/tokens/${id}/refresh`, {});

export const revokeOAuthToken = (id: string) =>
  apiPost<void>(`/api/v1/oauth/tokens/${id}/revoke`, {});

// ── Webhook Subscription API ──

export const getWebhookSubscriptions = (params: WebhookSubscriptionFilters = {}) =>
  apiGet<PagedResponse<WebhookSubscription>>('/api/v1/webhooks/subscriptions', params);

export const getWebhookSubscription = (id: string) =>
  apiGet<WebhookSubscription>(`/api/v1/webhooks/subscriptions/${id}`);

export const createWebhookSubscription = (data: CreateWebhookSubscriptionRequest) =>
  apiPost<WebhookSubscription>('/api/v1/webhooks/subscriptions', data);

export const updateWebhookSubscription = (id: string, data: UpdateWebhookSubscriptionRequest) =>
  apiPut<WebhookSubscription>(`/api/v1/webhooks/subscriptions/${id}`, data);

export const deleteWebhookSubscription = (id: string) =>
  apiDelete(`/api/v1/webhooks/subscriptions/${id}`);

export const testWebhookSubscription = (id: string) =>
  apiPost<WebhookTestResult>(`/api/v1/webhooks/subscriptions/${id}/test`, {});

// ── Webhook Event API ──

export const getWebhookEvents = (params: WebhookEventFilters = {}) =>
  apiGet<PagedResponse<WebhookEventInfo>>('/api/v1/webhooks/events', params);

export const getWebhookEvent = (id: string) =>
  apiGet<WebhookEventInfo>(`/api/v1/webhooks/events/${id}`);

export const retryWebhookEvent = (id: string) =>
  apiPost<WebhookEventInfo>(`/api/v1/webhooks/events/${id}/retry`, {});
